<template>
  <div class="flex flex-col gap-3 text-left">
    <p class="text-xs text-neutral50 m-0">
      Define qual fonte vence quando há mais de um registro para a mesma perna.
      <b>Check-in e check-out são resolvidos separadamente</b> — o horário efetivo pode vir de
      fontes diferentes no mesmo plantão. Arraste para reordenar (ou use ↑/↓ com o item focado).
    </p>

    <div
      class="rounded-lg border border-gray-200 overflow-hidden"
      data-testid="source-precedence-list"
      :data-draggable="String(!disabled)"
      role="list"
    >
      <draggable
        :model-value="orderedKinds"
        :item-key="itemKey"
        handle=".source-precedence-handle"
        ghost-class="bg-primary5"
        animation="200"
        :disabled="disabled"
        tag="div"
        @update:model-value="applyOrder"
      >
        <template #item="{ element, index }">
          <div
            class="flex items-center gap-3 border-b border-gray-100 bg-white px-3 py-2.5 last:border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary20"
            role="listitem"
            :data-testid="`source-precedence-item-${element}`"
            :draggable="!disabled"
            :tabindex="disabled ? -1 : 0"
            :aria-label="`${SOURCE_PRECEDENCE_NAMES[element as EffectiveSourceKind]}, posição ${index + 1} de ${orderedKinds.length + 1}`"
            @keydown="onItemKeydown($event, index)"
          >
            <SVGIcon
              :path="mdiDragVertical"
              aria-hidden="true"
              class="source-precedence-handle h-5 w-5 shrink-0 text-neutralLight"
              :class="disabled ? 'opacity-30' : 'cursor-grab'"
            />
            <span
              class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-primary5 text-xs font-extrabold text-primary40"
            >
              {{ index + 1 }}
            </span>
            <span class="flex flex-col">
              <span
                class="text-sm font-semibold text-neutral50"
                :data-testid="`source-precedence-item-${element}-name`"
              >
                {{ SOURCE_PRECEDENCE_NAMES[element as EffectiveSourceKind] }}
              </span>
              <span class="text-xs text-neutral50/70">
                {{ SOURCE_PRECEDENCE_DESCRIPTIONS[element as EffectiveSourceKind] }}
              </span>
            </span>
          </div>
        </template>
      </draggable>

      <!--
        "Manual (legado)" é somente histórico (Q4): checks lançados à mão no painel antes do
        bloqueio. Fica FORA do `draggable` para que nem o mouse nem o teclado consigam tirá-lo
        do fim — e por isso também não faz parte do `modelValue`.
      -->
      <div
        class="flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-3 py-2.5 text-neutralLight"
        role="listitem"
        :data-testid="`source-precedence-item-${PINNED_SOURCE_KIND}`"
        draggable="false"
        tabindex="-1"
        @keydown="onPinnedKeydown"
      >
        <SVGIcon :path="mdiLock" class="h-5 w-5 shrink-0 opacity-40" />
        <span
          class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-gray-200 text-xs font-extrabold text-gray-500"
        >
          {{ orderedKinds.length + 1 }}
        </span>
        <span class="flex flex-col">
          <span
            class="text-sm font-medium text-gray-500"
            :data-testid="`source-precedence-item-${PINNED_SOURCE_KIND}-name`"
          >
            {{ SOURCE_PRECEDENCE_NAMES[PINNED_SOURCE_KIND] }}
          </span>
          <span class="text-xs text-gray-400">
            {{ SOURCE_PRECEDENCE_DESCRIPTIONS[PINNED_SOURCE_KIND] }}
          </span>
        </span>
      </div>
    </div>

    <div v-if="!disabled" class="flex flex-wrap items-center gap-3">
      <button
        type="button"
        class="rounded-md border border-primary30 px-3 py-1.5 text-xs font-bold text-primary30"
        data-testid="source-precedence-restore-default"
        @click="restoreDefault"
      >
        ↺ Restaurar padrão
      </button>
      <span class="text-xs text-neutral50 m-0">
        Empate entre duas fontes do mesmo nível: vence o registro <b>mais recente</b>.
      </span>
    </div>

    <div
      class="flex gap-2 rounded-lg border border-negative/40 bg-negative/5 px-3 py-2.5 text-xs text-negative"
      data-testid="source-precedence-impact-warning"
    >
      <span aria-hidden="true">⚠️</span>
      <span>
        <b>Alterar a precedência muda o horário efetivo de plantões futuros e o valor faturado.</b>
        A ordem passa a valer para eventos posteriores ao salvamento — plantões já resolvidos não
        são recalculados.
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import draggable from 'vuedraggable'
import { mdiDragVertical, mdiLock } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import {
  DEFAULT_SOURCE_PRECEDENCE,
  PINNED_SOURCE_KIND,
  SOURCE_PRECEDENCE_DESCRIPTIONS,
  SOURCE_PRECEDENCE_NAMES,
  sanitizeSourcePrecedence
} from './sourcePrecedence'

/**
 * Ranking de precedência das fontes do horário efetivo (M4, seção "Precedência das
 * fontes"). Reordenável por drag & drop E por teclado (↑/↓ com o item focado) — o
 * drag sozinho deixaria a configuração inacessível a quem navega por teclado.
 */
const props = withDefaults(
  defineProps<{
    /** Ordem configurável, SEM `MANUAL_LEGACY`/`LEGACY`. Vazio = ordem de fábrica. */
    modelValue?: EffectiveSourceKind[] | null
    /** Cadeado da Q31: sem drag, sem teclado e sem "Restaurar padrão". */
    disabled?: boolean
  }>(),
  { modelValue: () => [...DEFAULT_SOURCE_PRECEDENCE], disabled: false }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: EffectiveSourceKind[]): void
}>()

const orderedKinds = computed(() => sanitizeSourcePrecedence(props.modelValue))

/** A lista é de strings: a chave do `draggable` é o próprio valor. */
const itemKey = (kind: EffectiveSourceKind) => kind

const applyOrder = (value: EffectiveSourceKind[]) => {
  if (props.disabled) return
  emit('update:modelValue', sanitizeSourcePrecedence(value))
}

const move = (from: number, to: number) => {
  if (to < 0 || to >= orderedKinds.value.length) return
  const next = [...orderedKinds.value]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  applyOrder(next)
}

const onItemKeydown = (event: KeyboardEvent, index: number) => {
  if (props.disabled) return
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
  event.preventDefault()
  move(index, event.key === 'ArrowUp' ? index - 1 : index + 1)
}

/** A linha travada engole as setas: mexer nela não pode emitir ordem nenhuma. */
const onPinnedKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') event.preventDefault()
}

const restoreDefault = () => applyOrder([...DEFAULT_SOURCE_PRECEDENCE])
</script>
